import React, { useState, useMemo } from "react";
import {
  CheckCircle,
  Users,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  Edit,
  Check,
  X,
  ShieldAlert,
  Target,
} from "../../../../components/icons";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Match } from "../../types";
import type { Player } from "../../types";
import { ACTION_FLOWS } from "../../constants";
import { formatDisplayName } from "../../../../lib/nameFormat";
import type { DeletePendingEventOutcome } from "../../hooks/useCockpitEventHandlers";
import {
  onFieldIdsBeforeEvent,
  validateSubstitutionEdit,
  type SubstitutionEditWarning,
} from "../../utils/substitutionEdit";
import {
  isPlacementEligible,
  isPlacementMandatory,
  type GoalPlacement,
} from "../../utils/goalFrame";
import GoalFramePlacement from "../molecules/GoalFramePlacement";
import OwnGoalPlacementHeader from "../molecules/OwnGoalPlacementHeader";
import { sameTeamId } from "../../utils/teamId";
import { dedupeByStableIdentity } from "../../hooks/useLogIntegrityChecks";
import { useLiveScore } from "../../hooks/useLiveScore";

// ─── Props ──────────────────────────────────────────────────────────────

interface ReviewViewProps {
  match: Match | null;
  liveEvents: MatchEvent[];
  /** PMA-COCK-205 (EPIC-43, judge round 1, P7): folded into the displayed
   * list (deduped against `liveEvents`) so an event awaiting ack-timeout
   * resend does not vanish from Review for its whole pending window — see
   * `filteredEvents`/`combinedEvents` below. Optional, defaults to `[]` —
   * every existing caller/test that only cares about `liveEvents` scenarios
   * needs no change. */
  queuedEvents?: MatchEvent[];
  isAdmin: boolean;
  onUpdateEventData?: (
    event: MatchEvent,
    updates: Partial<MatchEvent>,
  ) => Promise<void> | void;
  onUpdateEventNotes?: (
    event: MatchEvent,
    notes: string | null,
  ) => Promise<void> | void;
  onDeleteEvent?: (event: MatchEvent) => Promise<void> | void;
  /** R1 / PMA-COCK-076, restored here per PMA-COCK-112 (this trigger lived
   * exclusively inside LiveEventFeed's event row until PMA-COCK-111 removed
   * that component from the cockpit's render path). Not gated by isAdmin —
   * VAR overturns happen live and must be usable by whoever is operating
   * the cockpit. */
  onVarCornerToGoalKick?: (event: MatchEvent) => Promise<void> | void;
  /** PMA-COCK-113: deletes a PENDING (unacked, no `_id`) event from the
   * offline queue. Not gated by isAdmin — a pending event was never
   * persisted server-side, so removing it locally requires no server
   * authority (mirrors the pre-PMA-COCK-111 LiveEventFeed behavior). Returns
   * a status rather than void because the underlying handler re-checks the
   * event's pending/confirmed state at call time — see `handleDeletePendingClick`
   * below for why and how the "already-confirmed" case is handled. */
  onDeletePendingEvent?: (clientId: string) => DeletePendingEventOutcome;
  /** Sent-off players, used only to warn on a substitution correction. */
  expelledPlayerIds?: ReadonlySet<string>;
  /** PMA-COCK-182: review edit of the goal-mouth placement. Not gated by
   * isAdmin (same VAR-correction precedent as `onVarCornerToGoalKick`). */
  onUpdateGoalPlacement?: (
    event: MatchEvent,
    placement: GoalPlacement,
  ) => Promise<void> | void;
  t: any;
}

// ─── Helpers (mirrors LiveEventFeed) ────────────────────────────────────

const getPlayerName = (
  playerId: string | undefined,
  match: Match | null,
): string => {
  if (!playerId || !match) return "—";
  const allPlayers = [...match.home_team.players, ...match.away_team.players];
  const player = allPlayers.find((p) => p.id === playerId);
  return player
    ? `#${player.jersey_number} ${formatDisplayName(player.full_name)}`
    : playerId;
};

const getTeamName = (teamId: string | null, match: Match | null): string => {
  if (!teamId || !match) return "—";
  if (teamId === match.home_team.id) return match.home_team.short_name;
  if (teamId === match.away_team.id) return match.away_team.short_name;
  return teamId;
};

const getDisplayType = (event: MatchEvent): string => {
  if (event.type === "Shot" && event.data?.outcome === "Goal") return "Goal";
  return event.type;
};

/**
 * T13 (EPIC-61 criterion 10, cockpit half, client wave 2026-09-19). Same
 * "is this a scored goal" test `getDisplayType` above already uses —
 * whether this row's own-goal/penalty suffix should come from
 * `useLiveScore`'s `formatGoalScorer` instead of the generic `getPlayerName`.
 */
const isScoredGoalEvent = (event: MatchEvent): boolean =>
  event.type === "Shot" && event.data?.outcome === "Goal";

const getEventKey = (event: MatchEvent): string =>
  event._id || event.client_id || event.timestamp;

/** R1 / PMA-COCK-076 (mirrors LiveEventFeed.tsx): true once a Corner Kick has
 * been VAR-corrected to a Goal Kick in place. */
const isVarCornerToGoalKickEvent = (event: MatchEvent): boolean =>
  event.type === "SetPiece" &&
  event.data?.set_piece_type === "Goal Kick" &&
  event.data?.var_overturned_from === "Corner";

/** True for a Corner Kick event still eligible for the one-tap VAR
 * correction (not yet swapped, and persisted so the PUT edit path applies). */
const isCorrectableCornerKick = (event: MatchEvent): boolean =>
  Boolean(event._id) &&
  event.type === "SetPiece" &&
  event.data?.set_piece_type === "Corner";

/** PMA-COCK-182: true for a row that shows the goal-frame placement button —
 * a qualifying shot that has been persisted (`_id` — an unsynced event has
 * nothing yet to PUT against). */
const showsPlacementButton = (event: MatchEvent): boolean =>
  Boolean(event._id) && isPlacementEligible(event);

/** PMA-COCK-184: true for a row whose MISSING placement counts against the
 * "shots without placement" counter/filter — same persisted gate as the
 * button, but `isPlacementMandatory` excludes off-target shots (their
 * placement is optional; the button still shows on those rows via
 * `showsPlacementButton`). */
const showsMandatoryPlacement = (event: MatchEvent): boolean =>
  Boolean(event._id) && isPlacementMandatory(event);

const hasPlacement = (event: MatchEvent): boolean =>
  Boolean(event.data?.goal_placement);

const EVENT_TYPE_KEYS = Object.keys(ACTION_FLOWS);

/** i18n keys + English fallbacks for the substitution-correction warnings. */
const SUB_WARNING_KEYS: Record<SubstitutionEditWarning, string> = {
  samePlayer: "subWarnSamePlayer",
  playerInAlreadyOnPitch: "subWarnPlayerInAlreadyOnPitch",
  playerOutNotOnPitch: "subWarnPlayerOutNotOnPitch",
  playerInExpelled: "subWarnPlayerInExpelled",
  playerOutExpelled: "subWarnPlayerOutExpelled",
};

const SUB_WARNING_FALLBACKS: Record<SubstitutionEditWarning, string> = {
  samePlayer: "The same player cannot come on and go off.",
  playerInAlreadyOnPitch: "This player was already on the pitch.",
  playerOutNotOnPitch: "This player was not on the pitch at this moment.",
  playerInExpelled: "This player has been sent off.",
  playerOutExpelled: "This player had already been sent off.",
};

// ─── Component ──────────────────────────────────────────────────────────

const ReviewView: React.FC<ReviewViewProps> = ({
  match,
  liveEvents,
  queuedEvents = [],
  isAdmin,
  onUpdateEventData,
  onUpdateEventNotes,
  onDeleteEvent,
  onVarCornerToGoalKick,
  onDeletePendingEvent,
  expelledPlayerIds,
  onUpdateGoalPlacement,
  t,
}) => {
  // T13 (EPIC-61 criterion 10, cockpit half): reused rather than a second
  // own-goal formatter written locally — `useLiveScore` (the SAME hook
  // `PublicMatchTimeline.tsx`'s own "(AG)"/"(P)" suffix already comes
  // from) needs exactly `match`/`liveEvents`/`queuedEvents`, and this
  // component already receives all three as props. Two places deciding
  // independently what an own goal looks like is how the public and the
  // cockpit drift apart — this file had exactly that drift before this
  // fix (the Goal row showed a plain player name with nothing marking it
  // an own goal, unlike the public timeline).
  const { formatGoalScorer } = useLiveScore(match, liveEvents, queuedEvents);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Editing state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    type?: string;
    outcome?: string;
    player_id?: string;
    /** Substitution only: `data.player_on_id`, the player coming ON. */
    player_on_id?: string;
    team_id?: string;
    period?: number;
    match_clock?: string;
    notes?: string;
  }>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());
  const [varSwappingKeys, setVarSwappingKeys] = useState<Set<string>>(
    new Set(),
  );
  const [deletingPendingKeys, setDeletingPendingKeys] = useState<Set<string>>(
    new Set(),
  );

  // PMA-COCK-182: the event key whose inline goal-frame editor is expanded
  // (at most one at a time, mirrors `editingKey`), and in-flight saves.
  const [placementOpenKey, setPlacementOpenKey] = useState<string | null>(null);
  const [savingPlacementKeys, setSavingPlacementKeys] = useState<Set<string>>(
    new Set(),
  );

  // Filters
  const [filterType, setFilterType] = useState<string>("");
  const [filterTeam, setFilterTeam] = useState<string>("");
  const [filterPeriod, setFilterPeriod] = useState<string>("");
  const [filterMissingPlacement, setFilterMissingPlacement] =
    useState<boolean>(false);

  // All players for dropdowns
  const allPlayers: Player[] = useMemo(() => {
    if (!match) return [];
    return [...match.home_team.players, ...match.away_team.players];
  }, [match]);

  // PMA-COCK-205 (EPIC-43, judge round 1, P7): expireAck/
  // flushPendingAcksToQueue now remove an event from liveEvents before
  // re-adding it to queuedEvents (this same epic's producer fix), so for as
  // long as it awaits resend it would otherwise vanish from Review entirely
  // — liveEvents alone used to be sufficient, it no longer is.
  // dedupeByStableIdentity is the same guard useGoalTally/useDisciplinary/
  // AnalyticsView already use for the one remaining by-design overlap
  // (sendEvent()'s offline branch).
  //
  // `onFieldIdsBeforeEvent` below (substitution-edit validation) is
  // deliberately NOT switched to this combined array — editing is gated on
  // `event._id`, which a queued-only event never has, so that computation
  // stays scoped to the settled `liveEvents` chronology it already used.
  const combinedEvents = useMemo(
    () => dedupeByStableIdentity([...liveEvents, ...queuedEvents]),
    [liveEvents, queuedEvents],
  );

  // Filtered + paginated events (newest first)
  const filteredEvents = useMemo(() => {
    let events = [...combinedEvents].reverse();
    if (filterType) events = events.filter((e) => e.type === filterType);
    if (filterTeam)
      events = events.filter((e) => sameTeamId(e.team_id, filterTeam));
    if (filterPeriod)
      events = events.filter((e) => e.period === Number(filterPeriod));
    if (filterMissingPlacement)
      events = events.filter(
        (e) => showsMandatoryPlacement(e) && !hasPlacement(e),
      );
    return events;
  }, [
    combinedEvents,
    filterType,
    filterTeam,
    filterPeriod,
    filterMissingPlacement,
  ]);

  // PMA-COCK-182: "shots without placement" — a whole-match overview count,
  // independent of the type/team/period filters above (mirrors `periods`).
  // PMA-COCK-184: off-target shots (a missed penalty or an off-target
  // direct shot) are eligible for the placement button but excluded from
  // this count — their placement is optional, a skip means "beyond two
  // metres", not an omission. Reads `combinedEvents` for consistency with
  // `filteredEvents` above, though `showsMandatoryPlacement`'s own `_id`
  // gate means a queued-only event was never going to count here either
  // way (an unsynced event has no `_id` yet).
  const missingPlacementCount = useMemo(
    () =>
      combinedEvents.filter(
        (e) => showsMandatoryPlacement(e) && !hasPlacement(e),
      ).length,
    [combinedEvents],
  );

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const pageEvents = filteredEvents.slice(startIdx, startIdx + pageSize);

  // Unique periods for filter — reads `combinedEvents` too, or a period
  // whose events are all still queued would show in the list (via
  // `filteredEvents` above) but be unselectable here.
  const periods = useMemo(() => {
    const ps = new Set(combinedEvents.map((e) => e.period));
    return [...ps].sort((a, b) => a - b);
  }, [combinedEvents]);

  // Begin editing
  const beginEdit = (event: MatchEvent) => {
    const key = getEventKey(event);
    setEditingKey(key);
    setDraft({
      type: event.type,
      outcome: event.data?.outcome || "",
      // A Substitution stamps the outgoing player on BOTH `player_id` and
      // `data.player_off_id` (useCockpitSubstitutionFlow.ts). Prefer the
      // top-level field, fall back to the data one so an event written by
      // any other path still opens with the right player selected.
      player_id: event.player_id || event.data?.player_off_id || "",
      player_on_id: event.data?.player_on_id || "",
      team_id: event.team_id || "",
      period: event.period,
      match_clock: event.match_clock,
      notes: event.notes || "",
    });
  };

  const cancelEdit = () => setEditingKey(null);

  const saveEdit = async (event: MatchEvent) => {
    const key = getEventKey(event);
    if (savingKeys.has(key)) return;

    setSavingKeys((prev) => new Set(prev).add(key));
    try {
      // Build the updates payload
      const updates: Partial<MatchEvent> = {};

      if (draft.type && draft.type !== event.type) {
        updates.type = draft.type;
      }
      if (
        draft.player_id !== undefined &&
        draft.player_id !== (event.player_id || "")
      ) {
        updates.player_id = draft.player_id || undefined;
      }
      if (draft.team_id && !sameTeamId(draft.team_id, event.team_id)) {
        updates.team_id = draft.team_id;
      }
      if (draft.period !== undefined && draft.period !== event.period) {
        updates.period = draft.period;
      }
      if (draft.match_clock && draft.match_clock !== event.match_clock) {
        updates.match_clock = draft.match_clock;
      }

      // Handle outcome within data
      const currentOutcome = event.data?.outcome || "";
      if (draft.outcome !== undefined && draft.outcome !== currentOutcome) {
        updates.data = { ...event.data, outcome: draft.outcome || undefined };
      }

      // Substitution corrections write BOTH directions into `data`. The
      // outgoing player is mirrored onto the top-level `player_id` because
      // that is the field the rest of the app attributes the event by
      // (event feed, player stats, the log-integrity red-card nudge), while
      // `data.player_off_id` / `data.player_on_id` are what the on-field
      // roster replay reads. Letting those two disagree is how a corrected
      // substitution would show the right name and still move the wrong
      // player off the pitch.
      const editedType = draft.type || event.type;
      if (editedType === "Substitution") {
        const nextOff = draft.player_id || undefined;
        const nextOn = draft.player_on_id || undefined;
        const currentOff = event.data?.player_off_id;
        const currentOn = event.data?.player_on_id;
        if (nextOff !== currentOff || nextOn !== currentOn) {
          updates.data = {
            ...(updates.data ?? event.data),
            player_off_id: nextOff,
            player_on_id: nextOn,
          };
        }
        if (nextOff !== (event.player_id || undefined)) {
          updates.player_id = nextOff;
        }
      }

      // Notes
      if (draft.notes !== undefined) {
        const trimmedNotes = draft.notes.trim();
        const currentNotes = event.notes || "";
        if (trimmedNotes !== currentNotes) {
          if (onUpdateEventNotes && !onUpdateEventData) {
            await onUpdateEventNotes(event, trimmedNotes || null);
          } else {
            updates.notes = trimmedNotes || undefined;
          }
        }
      }

      if (Object.keys(updates).length > 0 && onUpdateEventData) {
        await onUpdateEventData(event, updates);
      }

      setEditingKey(null);
    } finally {
      setSavingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleDelete = async (event: MatchEvent) => {
    if (!onDeleteEvent) return;
    const key = getEventKey(event);
    if (deletingKeys.has(key)) return;
    setDeletingKeys((prev) => new Set(prev).add(key));
    try {
      await onDeleteEvent(event);
    } finally {
      setDeletingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleVarCornerToGoalKick = async (event: MatchEvent) => {
    if (!onVarCornerToGoalKick) return;
    const key = getEventKey(event);
    if (varSwappingKeys.has(key)) return;
    setVarSwappingKeys((prev) => new Set(prev).add(key));
    try {
      await onVarCornerToGoalKick(event);
    } finally {
      setVarSwappingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // PMA-COCK-182: saves a goal-frame placement from the inline review
  // editor. Collapses the editor on success (mirrors `saveEdit`'s
  // `setEditingKey(null)`) so the row goes back to its filled-button state.
  const handleSaveGoalPlacement = async (
    event: MatchEvent,
    placement: GoalPlacement,
  ) => {
    if (!onUpdateGoalPlacement) return;
    const key = getEventKey(event);
    if (savingPlacementKeys.has(key)) return;
    setSavingPlacementKeys((prev) => new Set(prev).add(key));
    try {
      await onUpdateGoalPlacement(event, placement);
      setPlacementOpenKey(null);
    } finally {
      setSavingPlacementKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Deletes a pending (unacked) event. `onDeletePendingEvent` re-checks the
  // event's pending/confirmed state at call time (see its own comment in
  // useCockpitEventHandlers.ts) rather than trusting this row's render-time
  // `event` object — an event can be acknowledged by the server in the gap
  // between this row's last render and the user's tap. If that happened,
  // the handler returns the event back to us WITHOUT touching local state
  // (removing it here would just hide a real, still-server-side event), and
  // we route it through the normal admin-gated confirmed-delete path
  // instead — the same path `review-event-delete` already uses. If the
  // caller isn't admin, there is nothing safe to do: the event is real now,
  // and non-admins can never delete confirmed events from this view — the
  // row re-renders on its own into a normal confirmed row (no longer
  // "Pending") once the store update that resolved the race propagates.
  const handleDeletePendingClick = async (event: MatchEvent) => {
    if (!onDeletePendingEvent || !event.client_id) return;
    const key = getEventKey(event);
    if (deletingPendingKeys.has(key)) return;
    setDeletingPendingKeys((prev) => new Set(prev).add(key));
    try {
      const outcome = onDeletePendingEvent(event.client_id);
      if (outcome.status === "already-confirmed" && isAdmin && onDeleteEvent) {
        await handleDelete(outcome.event);
      }
    } finally {
      setDeletingPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // Get available outcomes for a given event type
  const getOutcomesForType = (type: string): string[] => {
    const flow = ACTION_FLOWS[type];
    if (!flow) return [];
    return [...new Set(Object.values(flow.outcomes ?? {}).flat())];
  };

  // Determine which team's players to show when editing a player
  const getTeamPlayers = (teamId: string): Player[] => {
    if (!match) return [];
    if (teamId === match.home_team.id) return match.home_team.players;
    if (teamId === match.away_team.id) return match.away_team.players;
    return allPlayers;
  };

  return (
    <div
      className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden flex flex-col"
      data-testid="review-panel"
    >
      {/* Header */}
      <div className="border-b border-slate-700 p-4 bg-slate-900/50">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-100">
            {t("reviewTitle", "Event Review & Corrections")}
          </h2>
          <div className="flex items-center gap-2">
            <span
              className="text-sm font-medium text-slate-400 bg-slate-700 px-3 py-1 rounded-full border border-slate-600"
              data-testid="review-total"
            >
              {filteredEvents.length} {t("eventsLabel", "events")}
            </span>
            {missingPlacementCount > 0 && (
              <span
                className="text-xs font-semibold text-cyan-300 bg-cyan-900/30 px-2 py-1 rounded-full border border-cyan-700/50"
                data-testid="review-missing-placement-count"
              >
                {missingPlacementCount}{" "}
                {t("missingPlacementCount", "shots without placement")}
              </span>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {t("type", "Type")}
            </span>
            <select
              data-testid="review-filter-type"
              className="text-xs bg-slate-900 text-slate-200 border border-slate-700 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-500"
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">{t("all", "All")}</option>
              {EVENT_TYPE_KEYS.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {t("team", "Team")}
            </span>
            <select
              data-testid="review-filter-team"
              className="text-xs bg-slate-900 text-slate-200 border border-slate-700 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-500"
              value={filterTeam}
              onChange={(e) => {
                setFilterTeam(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">{t("all", "All")}</option>
              {match && (
                <>
                  <option value={match.home_team.id}>
                    {match.home_team.short_name}
                  </option>
                  <option value={match.away_team.id}>
                    {match.away_team.short_name}
                  </option>
                </>
              )}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {t("periodLabel", "Period")}
            </span>
            <select
              data-testid="review-filter-period"
              className="text-xs bg-slate-900 text-slate-200 border border-slate-700 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-500"
              value={filterPeriod}
              onChange={(e) => {
                setFilterPeriod(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">{t("all", "All")}</option>
              {periods.map((p) => (
                <option key={p} value={p}>
                  P{p}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            data-testid="review-filter-missing-placement"
            aria-pressed={filterMissingPlacement}
            onClick={() => {
              setFilterMissingPlacement((prev) => !prev);
              setCurrentPage(1);
            }}
            className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
              filterMissingPlacement
                ? "bg-cyan-900/40 text-cyan-300 border-cyan-700"
                : "text-slate-400 border-slate-700 hover:bg-slate-700"
            }`}
          >
            {t("filterMissingPlacement", "Missing placement")}
          </button>

          {/* Page size */}
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {t("show", "Show")}
            </span>
            {[10, 25, 50, 100].map((size) => (
              <button
                key={size}
                onClick={() => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                className={`text-xs px-2 py-1 rounded ${
                  pageSize === size
                    ? "bg-teal-900/40 text-teal-300 font-medium border border-teal-800"
                    : "text-slate-500 hover:bg-slate-700"
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Events list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1 max-h-[75vh] scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
        {filteredEvents.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-12">
            <Users size={48} className="mx-auto mb-3 text-slate-700" />
            <p>{t("noEvents", "No events yet")}</p>
          </div>
        ) : (
          pageEvents.map((event, idx) => {
            const eventKey = getEventKey(event);
            const isEditing = editingKey === eventKey;
            const isSaving = savingKeys.has(eventKey);
            const isDeleting = deletingKeys.has(eventKey);
            const displayType = getDisplayType(event);
            const teamName = getTeamName(event.team_id, match);
            // T13: a scored goal's player name carries the same " (AG)"/
            // " (P)" suffix convention the public timeline already shows,
            // via the shared `formatGoalScorer` — every other row (every
            // non-Shot-Goal type) is completely unaffected, still
            // `getPlayerName`.
            const playerName = isScoredGoalEvent(event)
              ? formatGoalScorer(event)
              : getPlayerName(
                  event.player_id ?? event.data?.trigger_player_id,
                  match,
                );
            const isPending = Boolean(event.client_id && !event._id);
            const canEdit =
              Boolean(onUpdateEventData) && Boolean(event._id) && isAdmin;
            const canDelete =
              Boolean(onDeleteEvent) && Boolean(event._id) && isAdmin;
            const isVarCorrected = isVarCornerToGoalKickEvent(event);
            const displayLabel = isVarCorrected
              ? t("eventVarCornerToGoalKick", "VAR: Corner → Goal Kick")
              : displayType;
            const showVarCornerAction =
              typeof onVarCornerToGoalKick === "function" &&
              isCorrectableCornerKick(event);
            const varSwapping = varSwappingKeys.has(eventKey);
            const isDeletingPending = deletingPendingKeys.has(eventKey);
            const showPlacementButton =
              Boolean(onUpdateGoalPlacement) && showsPlacementButton(event);
            const placement: GoalPlacement | undefined =
              event.data?.goal_placement;
            const isPlacementOpen = placementOpenKey === eventKey;
            const isSavingPlacement = savingPlacementKeys.has(eventKey);
            const isSubstitution = event.type === "Substitution";
            const editedType = isEditing
              ? draft.type || event.type
              : event.type;
            const isEditingSubstitution =
              isEditing && editedType === "Substitution";
            const subWarnings: SubstitutionEditWarning[] = isEditingSubstitution
              ? validateSubstitutionEdit({
                  playerOffId: draft.player_id,
                  playerOnId: draft.player_on_id,
                  onFieldIdsBefore: onFieldIdsBeforeEvent(
                    match,
                    liveEvents,
                    liveEvents.findIndex((e) => getEventKey(e) === eventKey),
                  ),
                  expelledPlayerIds,
                })
              : [];

            return (
              <div
                key={`${eventKey}-${startIdx + idx}`}
                data-testid="review-event-item"
                className={`border rounded-lg transition-all ${
                  isEditing
                    ? "border-teal-500/50 bg-teal-900/10 shadow-md ring-1 ring-teal-500/30"
                    : "border-slate-700 bg-slate-900/50 hover:border-slate-600"
                }`}
              >
                {isEditing ? (
                  /* ───── Inline edit form ───── */
                  <div className="p-3 space-y-3" data-testid="review-edit-form">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                      {/* Type */}
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                          {t("type", "Type")}
                        </label>
                        <select
                          data-testid="review-edit-type"
                          className="w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          value={draft.type || event.type}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              type: e.target.value,
                              outcome: "", // reset outcome when type changes
                            }))
                          }
                        >
                          {EVENT_TYPE_KEYS.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Outcome */}
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                          {t("outcome", "Outcome")}
                        </label>
                        <select
                          data-testid="review-edit-outcome"
                          className="w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          value={draft.outcome ?? ""}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              outcome: e.target.value,
                            }))
                          }
                        >
                          <option value="">
                            {t("selectOutcome", "— Select —")}
                          </option>
                          {getOutcomesForType(draft.type || event.type).map(
                            (o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ),
                          )}
                        </select>
                      </div>

                      {/* Team */}
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                          {t("team", "Team")}
                        </label>
                        <select
                          data-testid="review-edit-team"
                          className="w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          value={draft.team_id || event.team_id}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              team_id: e.target.value,
                              // Reset BOTH player selects: the substitute
                              // coming on belongs to the same team as the
                              // one going off, so a team change invalidates
                              // it too.
                              player_id: "",
                              player_on_id: "",
                            }))
                          }
                        >
                          {match && (
                            <>
                              <option value={match.home_team.id}>
                                {match.home_team.short_name}
                              </option>
                              <option value={match.away_team.id}>
                                {match.away_team.short_name}
                              </option>
                            </>
                          )}
                        </select>
                      </div>

                      {/* Player — labelled "Player Out" for a substitution,
                          where it carries the player LEAVING the pitch. */}
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                          {isEditingSubstitution
                            ? t("substitution.selectPlayerOff", "Player Off")
                            : t("player", "Player")}
                        </label>
                        <select
                          data-testid="review-edit-player"
                          className="w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          value={draft.player_id ?? event.player_id ?? ""}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              player_id: e.target.value,
                            }))
                          }
                        >
                          <option value="">
                            {t("teamEvent", "Team Event")}
                          </option>
                          {getTeamPlayers(draft.team_id || event.team_id).map(
                            (p) => (
                              <option key={p.id} value={p.id}>
                                #{p.jersey_number}{" "}
                                {formatDisplayName(p.full_name)}
                              </option>
                            ),
                          )}
                        </select>
                      </div>

                      {/* Player In — substitutions only. The incoming player
                          lives in `data.player_on_id`, which had no editor at
                          all until now, so a substitution logged against the
                          wrong shirt number could only be deleted and
                          re-entered. */}
                      {isEditingSubstitution && (
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                            {t("substitution.selectPlayerOn", "Player On")}
                          </label>
                          <select
                            data-testid="review-edit-player-in"
                            className="w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            value={draft.player_on_id ?? ""}
                            onChange={(e) =>
                              setDraft((prev) => ({
                                ...prev,
                                player_on_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">
                              {t("selectPlayer", "Select a Player")}
                            </option>
                            {getTeamPlayers(draft.team_id || event.team_id).map(
                              (p) => (
                                <option key={p.id} value={p.id}>
                                  #{p.jersey_number}{" "}
                                  {formatDisplayName(p.full_name)}
                                </option>
                              ),
                            )}
                          </select>
                        </div>
                      )}

                      {/* Period */}
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                          {t("periodLabel", "Period")}
                        </label>
                        <select
                          data-testid="review-edit-period"
                          className="w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          value={draft.period ?? event.period}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              period: Number(e.target.value),
                            }))
                          }
                        >
                          {[1, 2, 3, 4, 5].map((p) => (
                            <option key={p} value={p}>
                              P{p}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Clock */}
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                          {t("clock", "Clock")}
                        </label>
                        <input
                          type="text"
                          data-testid="review-edit-clock"
                          className="w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-teal-500"
                          value={draft.match_clock ?? event.match_clock}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              match_clock: e.target.value,
                            }))
                          }
                          placeholder="MM:SS"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                        {t("notes", "Notes")}
                      </label>
                      <textarea
                        data-testid="review-edit-notes"
                        className="w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                        rows={2}
                        value={draft.notes ?? ""}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        placeholder={t("notesPlaceholder", "Add a note...")}
                      />
                    </div>

                    {/* Substitution sanity warnings. Advisory only — the
                        save button stays enabled. A correction is made
                        because the log is already wrong, so blocking on
                        state derived from that same wrong log would trap the
                        operator (see `substitutionEdit.ts`). */}
                    {subWarnings.length > 0 && (
                      <div
                        data-testid="review-edit-sub-warning"
                        role="status"
                        className="rounded border border-amber-600/40 bg-amber-900/20 px-2.5 py-2 text-[11px] text-amber-200"
                      >
                        <ul className="space-y-0.5">
                          {subWarnings.map((warning) => (
                            <li
                              key={warning}
                              data-testid={`review-edit-sub-warning-${warning}`}
                            >
                              {t(
                                SUB_WARNING_KEYS[warning],
                                SUB_WARNING_FALLBACKS[warning],
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Save / Cancel */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="review-edit-save"
                        disabled={isSaving}
                        onClick={() => saveEdit(event)}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-teal-600/30 text-teal-200 border border-teal-500/40 hover:bg-teal-600/50 disabled:opacity-50 font-semibold"
                      >
                        <Check size={12} />
                        {isSaving
                          ? t("saving", "Saving…")
                          : t("saveChanges", "Save changes")}
                      </button>
                      <button
                        type="button"
                        data-testid="review-edit-cancel"
                        onClick={cancelEdit}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 font-semibold"
                      >
                        <X size={12} />
                        {t("cancel", "Cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ───── Display row ───── */
                  <div className="p-3 flex items-center gap-3">
                    {/* Clock & period */}
                    <div className="flex flex-col items-center shrink-0 w-16">
                      <span className="text-sm font-mono font-semibold text-slate-300">
                        {event.match_clock}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        P{event.period}
                      </span>
                    </div>

                    {/* Type badge */}
                    <span
                      data-testid={
                        isVarCorrected
                          ? "review-event-var-corner-label"
                          : undefined
                      }
                      className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                        displayType === "Goal"
                          ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                          : "bg-slate-800 text-slate-300 border-slate-700"
                      }`}
                    >
                      {displayLabel}
                    </span>

                    {/* Team */}
                    <span className="shrink-0 text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded border border-slate-700 font-medium">
                      {teamName}
                    </span>

                    {/* Player. A substitution names two people, so the row
                        shows both directions in the same ▲ on / ▼ off
                        language the public timeline and team sheet use —
                        without it the row read as a single anonymous name and
                        the operator could not tell which one was wrong. */}
                    {isSubstitution ? (
                      <span
                        className="flex-1 min-w-0 flex flex-col leading-tight"
                        data-testid="review-event-substitution-players"
                      >
                        <span className="text-xs text-emerald-300 truncate">
                          <span aria-hidden="true">▲</span>{" "}
                          <span className="sr-only">
                            {t("substitution.selectPlayerOn", "Player On")}
                            :{" "}
                          </span>
                          {getPlayerName(event.data?.player_on_id, match)}
                        </span>
                        <span className="text-xs text-red-300 truncate">
                          <span aria-hidden="true">▼</span>{" "}
                          <span className="sr-only">
                            {t("substitution.selectPlayerOff", "Player Off")}
                            :{" "}
                          </span>
                          {getPlayerName(
                            event.player_id ?? event.data?.player_off_id,
                            match,
                          )}
                        </span>
                      </span>
                    ) : (
                      <span className="flex-1 text-xs text-slate-300 truncate min-w-0">
                        {playerName}
                      </span>
                    )}

                    {/* Outcome */}
                    {event.data?.outcome && (
                      <span
                        className={`shrink-0 text-xs font-medium ${
                          event.data.outcome.toLowerCase().includes("goal") ||
                          event.data.outcome
                            .toLowerCase()
                            .includes("complete") ||
                          event.data.outcome
                            .toLowerCase()
                            .includes("success") ||
                          event.data.outcome.toLowerCase().includes("won")
                            ? "text-green-400"
                            : event.data.outcome
                                  .toLowerCase()
                                  .includes("incomplete") ||
                                event.data.outcome
                                  .toLowerCase()
                                  .includes("failed") ||
                                event.data.outcome
                                  .toLowerCase()
                                  .includes("lost")
                              ? "text-red-400"
                              : "text-slate-400"
                        }`}
                      >
                        {event.data.outcome}
                      </span>
                    )}

                    {/* Notes indicator. PMA-COCK-204: the note's own text is
                        rendered here (truncated, full text still available
                        via `title` on hover/overflow) -- previously this
                        badge was emoji-only with the note tucked into the
                        `title` attribute alone, which is not text content
                        (no visible confirmation of what was saved, and not
                        matched by a DOM text-content query either). */}
                    {event.notes && (
                      <span
                        className="shrink-0 flex items-center gap-1 max-w-[9rem] text-[10px] text-amber-400 bg-amber-900/20 px-1.5 py-0.5 rounded border border-amber-700/30"
                        title={event.notes}
                      >
                        <span aria-hidden="true">📝</span>
                        {/* Own leaf text node -- not concatenated with the
                            emoji -- so the note's exact text is queryable on
                            its own (both a strict/exact text match and a
                            substring one resolve identically here). */}
                        <span className="truncate">{event.notes}</span>
                      </span>
                    )}

                    {/* Status badges */}
                    {isPending && (
                      <span className="shrink-0 inline-flex items-center gap-1 text-[10px] bg-yellow-900/30 text-yellow-500 px-2 py-0.5 rounded-full border border-yellow-900/50">
                        {t("pending", "Pending")}
                        {onDeletePendingEvent && (
                          <button
                            type="button"
                            data-testid="review-event-delete-pending"
                            onClick={() => handleDeletePendingClick(event)}
                            disabled={isDeletingPending}
                            className="text-yellow-500 hover:text-red-400 p-0.5 -mr-1 rounded hover:bg-slate-800 transition-colors disabled:opacity-50"
                            title={t("deletePending", "Delete pending event")}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </span>
                    )}
                    {event._confirmed && (
                      <span className="shrink-0">
                        <CheckCircle size={14} className="text-green-500" />
                      </span>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      {showVarCornerAction && (
                        <button
                          type="button"
                          data-testid="review-event-var-corner-to-goal-kick"
                          onClick={() => handleVarCornerToGoalKick(event)}
                          disabled={varSwapping}
                          className="text-amber-300 hover:text-amber-200 p-1.5 rounded hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t(
                            "varCornerToGoalKickAction",
                            "VAR: convert to Goal Kick",
                          )}
                        >
                          <ShieldAlert size={14} />
                        </button>
                      )}
                      {showPlacementButton && (
                        <button
                          type="button"
                          data-testid="review-event-goal-placement"
                          aria-pressed={Boolean(placement)}
                          onClick={() =>
                            setPlacementOpenKey((prev) =>
                              prev === eventKey ? null : eventKey,
                            )
                          }
                          className={`p-1.5 rounded hover:bg-slate-800 transition-colors ${
                            placement
                              ? "text-cyan-300 hover:text-cyan-200"
                              : "text-slate-500 hover:text-cyan-300"
                          }`}
                          title={
                            placement
                              ? t(
                                  "editGoalPlacement",
                                  "Edit where the ball went",
                                )
                              : t("addGoalPlacement", "Add where the ball went")
                          }
                        >
                          <Target size={14} />
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          data-testid="review-event-edit"
                          onClick={() => beginEdit(event)}
                          className="text-slate-400 hover:text-teal-300 p-1.5 rounded hover:bg-slate-800 transition-colors"
                          title={t("editEvent", "Edit event")}
                        >
                          <Edit size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          data-testid="review-event-delete"
                          onClick={() => handleDelete(event)}
                          disabled={isDeleting}
                          className="text-slate-400 hover:text-rose-300 p-1.5 rounded hover:bg-slate-800 transition-colors disabled:opacity-50"
                          title={t("deleteEvent", "Delete event")}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {isPlacementOpen && onUpdateGoalPlacement && (
                  <div
                    className="border-t border-slate-700 p-3 bg-slate-900/60"
                    data-testid="review-event-goal-placement-panel"
                  >
                    {/* EPIC-98 (item 5/AC5): names whose net an own goal
                        actually was — this panel otherwise shows only the
                        frame, with no text saying which goal it is. Renders
                        nothing for a non-own-goal event. */}
                    {match && (
                      <OwnGoalPlacementHeader
                        event={event}
                        match={match}
                        t={t}
                        className="mb-2"
                      />
                    )}
                    <GoalFramePlacement
                      value={placement}
                      outcome={event.data?.outcome}
                      disabled={isSavingPlacement}
                      size={220}
                      source="review"
                      onPlace={(next) => handleSaveGoalPlacement(event, next)}
                      t={t}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-slate-700 p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">
              {t("showing", "Showing")} {startIdx + 1}-
              {Math.min(startIdx + pageSize, filteredEvents.length)}{" "}
              {t("of", "of")} {filteredEvents.length}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-200"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-200"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs font-medium text-slate-300 px-3">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-200"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-200"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewView;
